/**
 * Le mandat d'étude sur le wire — chunk 9.BJ.1.
 *
 * Le mandat appartient à l'ENTREPRISE : c'est elle que le texte nomme « preneur »,
 * et c'est pour elle que le mandat révoque tout mandat antérieur. Un dossier le
 * référence par provenance, sans en être la portée — un autre dossier du même
 * client réutilise le mandat tant qu'il est valide.
 *
 * ## Ce qui ne voyage PAS
 *
 * `signature_ip_hash` reste côté serveur. C'est une donnée personnelle déjà
 * hachée, dont la valeur est probatoire et non consultative : l'afficher
 * n'aiderait personne et l'exposerait à toute copie d'écran. Elle vit dans la
 * table et dans l'audit, où on la retrouve si une signature est contestée.
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";
import { QuoteDeliveryPolicySchema } from "./tenants.js";

/** Un mandat tel que les routes le renvoient. */
export const MandateSchema = z.object({
  version_number: z.number().int().nullable().optional(),
  artifact_available: z.boolean().optional(),
  id: UuidSchema,
  tenant_id: UuidSchema,
  company_id: UuidSchema,
  /** Le dossier depuis lequel le mandat a été demandé. Provenance, pas portée. */
  origin_project_id: UuidSchema.nullable(),
  /** Signé chez nous. Exclusif avec `source_file_id`. */
  form_response_id: UuidSchema.nullable(),
  /** Signé ailleurs et déposé. Exclusif avec `form_response_id`. */
  source_file_id: UuidSchema.nullable(),
  /** Le PDF figé qui fait foi. */
  artifact_file_id: UuidSchema.nullable(),
  signed_at: z.string(),
  signed_by_name: z.string(),
  signed_by_email: z.string().nullable(),
  signed_by_title: z.string().nullable(),
  valid_from: z.string(),
  expires_at: z.string(),
  revoked_at: z.string().nullable(),
  revoked_reason: z.string().nullable(),
  revoked_by_mandate_id: UuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  /**
   * Dérivé, jamais stocké : `revoked_at IS NULL AND expires_at > now()`. Le
   * calculer à la lecture évite qu'un mandat devienne « encore valide » parce
   * que personne n'a repassé un drapeau.
   */
  is_valid: z.boolean(),
});
export type Mandate = z.infer<typeof MandateSchema>;

/**
 * Enregistrer un mandat signé AILLEURS et déposé.
 *
 * Le canal « signé chez nous » n'a pas d'entrée ici : il naît de la soumission
 * du formulaire par le client, côté public. Exposer une route qui fabriquerait
 * une signature au nom d'un tiers serait exactement ce qu'un mandat ne doit pas
 * permettre.
 */
export const RecordMandateSchema = z.object({
  company_id: UuidSchema,
  /** Le dossier qui a déclenché la demande, pour la provenance. */
  origin_project_id: UuidSchema.nullable().optional(),
  /** Le PDF signé, déjà téléversé. */
  source_file_id: UuidSchema,
  /** Quand le client a signé — pas quand on l'enregistre. */
  signed_at: z.string().datetime({ offset: true }),
  signed_by_name: z.string().trim().min(1).max(200),
  signed_by_email: z.string().email().max(320).nullable().optional(),
  signed_by_title: z.string().trim().max(200).nullable().optional(),
});
export type RecordMandateInput = z.infer<typeof RecordMandateSchema>;

export const RevokeMandateSchema = z.object({
  /** Obligatoire : une pièce de conformité qui disparaît sans raison écrite laisse une question sans réponse. */
  reason: z.string().trim().min(1).max(500),
});
export type RevokeMandateInput = z.infer<typeof RevokeMandateSchema>;

export const MandateFilterSchema = z.object({
  company_id: UuidSchema,
});

// -----------------------------------------------------------------------------
// Conformité d'envoi
// -----------------------------------------------------------------------------

/**
 * Les codes de blocage d'un envoi, tels qu'ils voyagent.
 *
 * Ce sont des codes du registre bilingue : l'interface les traduit avec le même
 * mécanisme que n'importe quelle erreur d'API, et n'a donc aucune phrase à
 * écrire ni aucune règle à rejouer.
 */
export const SEND_BLOCKER_CODES = [
  "QUOTE_REQUEST_SEND_MANDATE_MISSING",
  "QUOTE_REQUEST_SEND_MANDATE_EXPIRED",
  "QUOTE_REQUEST_SEND_REQUIREMENTS_UNMET",
  "QUOTE_REQUEST_SEND_SPEC_REQUIRED",
  "QUOTE_REQUEST_SPEC_PURPOSE_INVALID",
  "QUOTE_REQUEST_SEND_MANDATE_ARTIFACT_MISSING",
  "QUOTE_REQUEST_SEND_CENSUS_REQUIRED",
  "QUOTE_REQUEST_SEND_CENSUS_NOT_LOCKED",
  "QUOTE_REQUEST_SEND_COMMISSION_REQUIRED",
  "QUOTE_REQUEST_SEND_ATTACHMENT_MISSING",
  "QUOTE_REQUEST_SEND_SPEC_STALE",
  "QUOTE_REQUESTS_NO_RECIPIENTS",
  "QUOTE_REQUEST_SEND_RECIPIENT_WITHOUT_EMAIL",
  "QUOTE_REQUEST_SEND_EMAIL_BLOCKED",
  "QUOTE_REQUESTS_EXPECTED_RESPONSE_REQUIRED",
  "QUOTE_REQUEST_SEND_ATTACHMENTS_TOO_LARGE",
  "QUOTE_REQUEST_SEND_CENSUS_EXPORT_REQUIRED",
  "QUOTE_REQUEST_SEND_CENSUS_EXPORT_STALE",
] as const;
export type SendBlockerCode = (typeof SEND_BLOCKER_CODES)[number];

/**
 * Un blocage, avec ce qu'il faut pour AGIR.
 *
 * `catchall` plutôt qu'une union stricte par code : les détails varient d'un
 * blocage à l'autre (assureurs à corriger, cahier à régénérer, requis à
 * satisfaire), et le serveur reste l'autorité sur leur forme. L'interface lit ce
 * dont elle a besoin et ignore le reste.
 */
export const SendBlockerSchema = z
  .object({ code: z.enum(SEND_BLOCKER_CODES) })
  .catchall(z.unknown());
export type SendBlocker = z.infer<typeof SendBlockerSchema>;

/**
 * Le verdict de conformité d'un envoi.
 *
 * Servi par `GET /v1/quote-requests/:id/readiness`, et produit par la MÊME
 * fonction serveur que celle qui refuse `POST /:id/send`. L'écran ne peut donc
 * pas promettre un envoi que la route refuserait.
 */
export const SendReadinessSchema = z.object({
  quote_request_id: UuidSchema,
  can_send: z.boolean(),
  delivery: z
    .object({
      company_name: z.string().nullable(),
      expected_response_by: z.string().nullable(),
      requested_commission_rate: z.number().nullable(),
      insurer_email_note: z.string().nullable(),
      policy: QuoteDeliveryPolicySchema,
      /** Saved-data preview only; sending re-evaluates readiness and snapshots. */
      email_previews: z
        .array(
          z.object({
            recipient_id: UuidSchema,
            insurer_code: z.string(),
            to_email: z.string().nullable(),
            language: z.enum(["fr", "en"]),
            rendered: z
              .object({ subject: z.string(), text: z.string(), html: z.string() })
              .nullable(),
          }),
        )
        .optional(),
      attachments: z.array(
        z.object({
          file_id: UuidSchema,
          sha256: z.string().nullable(),
          file_name: z.string(),
          size_bytes: z.number(),
        }),
      ),
    })
    .optional(),
  workflow: z
    .object({
      /** Prochaine étape recommandée, jamais une permission de navigation. */
      step: z.enum(["authorization", "specification", "census", "insurers", "review"]),
      authorization_complete: z.boolean(),
      specification_complete: z.boolean(),
    })
    .optional(),
  /** Tous les blocages, dans l'ordre où on les répare — pas seulement le premier. */
  blockers: z.array(SendBlockerSchema),
  /**
   * L'état de l'effectif transmis — **`null` quand le dossier n'en a pas**.
   *
   * Les blocages ne suffisent pas ici : « prêt » n'en produit aucun, et l'écran
   * ne saurait pas distinguer un export à jour d'un dossier sans effectif. Or
   * la différence compte — **un census verrouillé ne signifie pas qu'un export
   * existe**, et laisser l'écran le déduire serait précisément l'erreur.
   *
   * L'état est **calculé par le serveur**, par la même fonction qui refuse
   * l'envoi : l'écran l'affiche, il ne le rejuge pas.
   */
  census_export: z
    .object({
      /**
       * `inverifiable` n'est pas `perime` : l'un dit « l'effectif a changé »,
       * l'autre « on ne peut pas le savoir ». Les confondre ferait affirmer à
       * l'écran un fait qu'on n'a pas constaté.
       */
      etat: z.enum(["absent", "pret", "perime", "inverifiable"]),
      census_id: UuidSchema,
      artifact_file_id: UuidSchema.nullable(),
      rendered_at: z.string().nullable(),
    })
    .nullable()
    .optional(),
});
export type SendReadiness = z.infer<typeof SendReadinessSchema>;
